import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
} from 'recharts';
import { formatCurrency, formatPercent, formatMoney, signClass } from '../../hooks/useResultsAnalysis';
import { STRATEGY_LABELS } from '../../utils/constants/realEstateConstants';
import SameHorizonPanel from './SameHorizonPanel';

const COLORS = ['#00ff41', '#00d4ff', '#ffb000'];

export default function StrategyComparison({ analysis, selectedStrategy, recommendedStrategy, losing }) {
  const {
    annualizedROI, ltrTotalROIAnnualized, strTotalROIAnnualized,
    purchasePrice, downPayment, loanAmount, renovationCost, holdingPeriod,
    monthlyCarry, totalHoldingCosts, expectedSellingPrice,
    sellingCosts, totalInvestment, netProfit, roi,
    monthlyRent, annualCashFlow, ltrCashOnCashReturn, capRate, vacancyRate,
    nightlyRate, occupancyRate, annualStrRevenue, annualStrCashFlow, strCashOnCashReturn,
    investmentBreakdown, downPaymentPercent, interestRate, sellingCostPercent,
    riskLabels, ltrCoverage, strCoverage,
    netProfit: flipProfit, rentalProfit, strTotalProfit, renovationMonths,
  } = analysis;
  // Why the top scorer scored, and what it costs to hold: a rental can lead on
  // its five-year return while the rent does not cover the bills.
  const rentalCash = { ltr: annualCashFlow, str: annualStrCashFlow }[recommendedStrategy];
  const fiveYearProfit = { ltr: rentalProfit, str: strTotalProfit }[recommendedStrategy];
  const bannerDetail = (() => {
    if (recommendedStrategy === 'flip') {
      return `Projected profit of ${formatMoney(flipProfit)} when it sells after ${holdingPeriod} months.`;
    }
    const wait = renovationMonths > 0 ? ` No rent comes in during the ${renovationMonths} months of renovation.` : '';
    if (rentalCash < 0) {
      return `Once rented, the income doesn't cover the loan and running costs, so about ${formatMoney(Math.abs(rentalCash) / 12)} a month comes out of your pocket. Its projected five-year profit of ${formatMoney(fiveYearProfit)} depends on the value at sale, not on the rent.${wait}`;
    }
    return `Once rented, it should bring in about ${formatMoney(rentalCash / 12)} a month after the loan and running costs, with a five-year profit of ${formatMoney(fiveYearProfit)} including the sale.${wait}`;
  })();
  const outOfPocket = recommendedStrategy !== 'flip' && rentalCash < 0;
  const riskTone = (label) => ({ Lower: 'text-terminal-green', Moderate: 'text-terminal-amber', Higher: 'text-terminal-red' }[label]);
  const coverageText = (ratio) => (ratio == null ? 'No loan' : `${ratio.toFixed(2)}x`);
  const allLose = losing === 'all';
  const bannerTone = losing || outOfPocket ? 'text-terminal-amber' : 'text-terminal-green';

  return (
    <>
      {/* Recommendation Banner */}
      <div className="bg-surface-elevated border-l-4 border-green-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-terminal-green" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className={`text-sm font-medium ${bannerTone}`}>Top-Scoring Strategy: {STRATEGY_LABELS[recommendedStrategy]}</h3>
            <div className={`mt-2 text-sm ${bannerTone}`}>
              <p>
                {allLose
                  ? `Every strategy loses money on these numbers. ${STRATEGY_LABELS[recommendedStrategy]} scores highest on your priorities, but its return is still negative.`
                  : losing === 'top'
                    ? `${STRATEGY_LABELS[recommendedStrategy]} scores highest on your priorities, but its return is negative and another strategy's isn't. Your weights favour something other than return, so compare the figures below.`
                    : `${STRATEGY_LABELS[recommendedStrategy]} scores highest on your priorities.`}
                {' '}{bannerDetail}
                {' '}This is a model estimate, not investment advice.
              </p>
            </div>
          </div>
        </div>
      </div>

      <SameHorizonPanel analysis={analysis} />

      {/* Normalized Strategy Comparison */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-txt-primary mb-4">Normalized Strategy Comparison</h2>
        <div className="bg-surface-elevated border border-blue-200 rounded p-4 mb-4">
          <p className="text-sm text-terminal-cyan">
            <strong>Total ROI (Annualized)</strong> is a compound annual rate on cash invested. Rentals assume a 5-year hold and a sale at the end: cash flow, value gain, and principal paydown, net of renovation, closing, and selling costs. The flip is annualized from its holding period.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Fix & Flip */}
          <div className={`bg-surface-primary p-4 rounded border-l-4 border-terminal-green ${selectedStrategy === 'flip' || selectedStrategy === 'all' ? '' : 'opacity-50'}`}>
            <h3 className="text-lg font-medium text-txt-primary mb-2 flex items-center">
              <span className="w-3 h-3 bg-terminal-green rounded-full mr-2"></span>
              Fix & Flip
            </h3>
            <dl className="space-y-2">
              <MetricRow label="Total ROI (Annualized)" value={`${formatPercent(annualizedROI)}%`} className={signClass(annualizedROI)} />
              <MetricRow label="Monthly Cash Flow" value="$0" />
              <MetricRow label="Net Profit" value={formatMoney(netProfit)} className={signClass(netProfit)} />
              <MetricRow label="Timeline" value={`${holdingPeriod} months`} />
              <MetricRow label="Risk (from profit margin and time)" value={riskLabels.flip} className={riskTone(riskLabels.flip)} />
              <MetricRow label="Workload" value="High" className="text-terminal-red" />
              <MetricRow label="Liquidity" value="High" />
            </dl>
          </div>

          {/* Long-Term Rental */}
          <div className={`bg-surface-primary p-4 rounded border-l-4 border-terminal-cyan ${selectedStrategy === 'ltr' || selectedStrategy === 'all' ? '' : 'opacity-50'}`}>
            <h3 className="text-lg font-medium text-txt-primary mb-2 flex items-center">
              <span className="w-3 h-3 bg-terminal-cyan rounded-full mr-2"></span>
              Long-Term Rental
            </h3>
            <dl className="space-y-2">
              <MetricRow label="Total ROI (Annualized)" value={`${formatPercent(ltrTotalROIAnnualized)}%`} className={signClass(ltrTotalROIAnnualized)} />
              <MetricRow label="Monthly Rent" value={`$${formatCurrency(monthlyRent)}`} />
              <MetricRow label="Monthly Cash Flow" value={formatMoney(annualCashFlow / 12)} className={signClass(annualCashFlow)} />
              <MetricRow label="Cash-on-Cash Return" value={`${formatPercent(ltrCashOnCashReturn)}%`} />
              <MetricRow label="Cap Rate (on Price Plus Renovation)" value={`${formatPercent(capRate)}%`} />
              <MetricRow label="Vacancy Rate" value={`${vacancyRate}%`} />
              <MetricRow label="Income / Loan Payments" value={coverageText(ltrCoverage)} className={ltrCoverage != null && ltrCoverage < 1 ? 'text-terminal-red' : ''} />
              <MetricRow label="Risk (from loan coverage)" value={riskLabels.ltr} className={riskTone(riskLabels.ltr)} />
              <MetricRow label="Workload" value="Low" className="text-terminal-green" />
            </dl>
          </div>

          {/* Short-Term Rental */}
          <div className={`bg-surface-primary p-4 rounded border-l-4 border-terminal-amber ${selectedStrategy === 'str' || selectedStrategy === 'all' ? '' : 'opacity-50'}`}>
            <h3 className="text-lg font-medium text-txt-primary mb-2 flex items-center">
              <span className="w-3 h-3 bg-terminal-amber rounded-full mr-2"></span>
              Short-Term Rental
            </h3>
            <dl className="space-y-2">
              <MetricRow label="Total ROI (Annualized)" value={`${formatPercent(strTotalROIAnnualized)}%`} className={signClass(strTotalROIAnnualized)} />
              <MetricRow label="Nightly Rate" value={`$${formatCurrency(nightlyRate)}`} />
              <MetricRow label="Occupancy Rate" value={`${occupancyRate}%`} />
              <MetricRow label="Monthly Revenue" value={`$${formatCurrency(annualStrRevenue / 12)}`} />
              <MetricRow label="Monthly Cash Flow" value={formatMoney(annualStrCashFlow / 12)} className={signClass(annualStrCashFlow)} />
              <MetricRow label="Cash-on-Cash Return" value={`${formatPercent(strCashOnCashReturn)}%`} />
              <MetricRow label="Income / Loan Payments" value={coverageText(strCoverage)} className={strCoverage != null && strCoverage < 1 ? 'text-terminal-red' : ''} />
              <MetricRow label="Risk (from loan coverage)" value={riskLabels.str} className={riskTone(riskLabels.str)} />
              <MetricRow label="Workload" value="High" className="text-terminal-red" />
            </dl>
          </div>
        </div>
      </div>

      {/* Investment Breakdown Chart */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-txt-primary mb-4">Investment Breakdown</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={investmentBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#00ff41"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {investmentBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip separator=": " offset={24} formatter={(value, name) => [`$${formatCurrency(value)}`, name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Analysis */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-txt-primary mb-4">Detailed Analysis</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <DetailCard title="Purchase Details">
              <MetricRow label="Purchase Price" value={`$${formatCurrency(purchasePrice)}`} />
              <MetricRow label="Down Payment" value={`$${formatCurrency(downPayment)}`} />
              <MetricRow label="Loan Amount" value={`$${formatCurrency(loanAmount)}`} />
            </DetailCard>

            <DetailCard title="Renovation Details">
              <MetricRow label="Renovation Cost" value={`$${formatCurrency(renovationCost)}`} />
              <MetricRow label="Holding Period" value={`${holdingPeriod} months`} />
              <MetricRow label="Monthly Carrying Costs" value={`$${formatCurrency(monthlyCarry)}`} />
            </DetailCard>

            <DetailCard title="Selling Details">
              <MetricRow label="Expected Selling Price" value={`$${formatCurrency(expectedSellingPrice)}`} />
              <MetricRow label="Selling Costs" value={`$${formatCurrency(sellingCosts)}`} />
              <MetricRow label="Total Holding Costs" value={`$${formatCurrency(totalHoldingCosts)}`} />
            </DetailCard>

            <DetailCard title="Investment Summary">
              <MetricRow label="Total Investment" value={`$${formatCurrency(totalInvestment)}`} />
              <MetricRow label="Net Profit" value={formatMoney(netProfit)} className={signClass(netProfit)} />
              <MetricRow label="ROI" value={`${formatPercent(roi)}%`} />
            </DetailCard>
          </div>
        </div>
      </div>

      {/* Property Details */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-txt-primary mb-4">Property Details</h2>
        <div className="bg-surface-primary p-4 rounded">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="text-lg font-medium text-txt-primary mb-2">Purchase</h3>
              <dl className="space-y-1">
                <MetricRow label="Purchase Price:" value={`$${formatCurrency(purchasePrice)}`} />
                <MetricRow label={`Down Payment (${downPaymentPercent}%):`} value={`$${formatCurrency(downPayment)}`} />
                <MetricRow label="Loan Amount:" value={`$${formatCurrency(loanAmount)}`} />
                <MetricRow label="Interest Rate:" value={`${interestRate}%`} />
              </dl>
            </div>
            <div>
              <h3 className="text-lg font-medium text-txt-primary mb-2">Renovation</h3>
              <dl className="space-y-1">
                <MetricRow label="Renovation Cost:" value={`$${formatCurrency(renovationCost)}`} />
                <MetricRow label="Timeline:" value={`${holdingPeriod} months`} />
                <MetricRow label="Monthly Carrying Costs:" value={`$${formatCurrency(monthlyCarry)}`} />
                <MetricRow label="Total Holding Costs:" value={`$${formatCurrency(totalHoldingCosts)}`} />
              </dl>
            </div>
            <div>
              <h3 className="text-lg font-medium text-txt-primary mb-2">Exit</h3>
              <dl className="space-y-1">
                <MetricRow label="After Repair Value:" value={`$${formatCurrency(expectedSellingPrice)}`} />
                <MetricRow label={`Selling Costs (${sellingCostPercent}%):`} value={`$${formatCurrency(sellingCosts)}`} />
                <MetricRow label="Monthly Rent:" value={`$${formatCurrency(monthlyRent)}`} />
                <MetricRow label="Nightly Rate:" value={`$${formatCurrency(nightlyRate)}`} />
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function MetricRow({ label, value, className = '' }) {
  return (
    <div className="flex justify-between">
      <dt className="text-sm text-txt-muted">{label}</dt>
      <dd className={`text-sm font-medium ${className || 'text-txt-primary'}`}>{value}</dd>
    </div>
  );
}

function DetailCard({ title, children }) {
  return (
    <div className="bg-surface-primary p-4 rounded">
      <h3 className="text-sm font-medium text-txt-muted">{title}</h3>
      <dl className="mt-2 space-y-2">{children}</dl>
    </div>
  );
}
